import type { CookieConsent, WindowWithCookiebot } from './types'

export const DEFAULT_COOKIE_CONSENT: CookieConsent = Object.freeze({
  necessary: true,
  preferences: false,
  statistics: false,
  marketing: false,
})

const COOKIEBOT_CONSENT_EVENTS = ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'] as const

export function createCookieConsentClient(
  getWindow: () => WindowWithCookiebot | undefined = () =>
    (typeof window === 'undefined' ? undefined : (window as unknown as WindowWithCookiebot)),
) {
  let cachedConsent: CookieConsent = DEFAULT_COOKIE_CONSENT

  function getCookieConsent(): CookieConsent {
    const consent = getWindow()?.Cookiebot?.consent
    if (!consent)
      return DEFAULT_COOKIE_CONSENT

    const nextConsent: CookieConsent = {
      necessary: consent.necessary !== false,
      preferences: consent.preferences === true,
      statistics: consent.statistics === true,
      marketing: consent.marketing === true,
    }

    if (!isSameConsent(cachedConsent, nextConsent))
      cachedConsent = nextConsent

    return cachedConsent
  }

  function subscribe(onChange: () => void) {
    const currentWindow = getWindow()
    if (!currentWindow)
      return () => {}

    COOKIEBOT_CONSENT_EVENTS.forEach(event => currentWindow.addEventListener(event, onChange))
    return () => {
      COOKIEBOT_CONSENT_EVENTS.forEach(event => currentWindow.removeEventListener(event, onChange))
    }
  }

  return { getCookieConsent, subscribe }
}

function isSameConsent(a: CookieConsent, b: CookieConsent) {
  return a.necessary === b.necessary
    && a.preferences === b.preferences
    && a.statistics === b.statistics
    && a.marketing === b.marketing
}

export const cookieConsentClient = createCookieConsentClient()
